import { signinPath } from '@crow/service-kit/web/session';
import type { AppBootstrap } from '@crowdex/agent-contracts/api';
import { isPanelView, type PanelView } from '@crowdex/host-kit/host';
import { AppToaster } from '@crowdex/ui/components/toaster';
import { useEffect, useState } from 'react';

import {
  Navigate,
  Outlet,
  type RouteObject,
  useLocation,
  useOutletContext,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';
import { AppHomePage } from '@/components/app-home-page';
import { ArtifactsPage } from '@/components/artifacts-page';
import { afterSignIn, SignInButton } from '@/components/auth-form';
import { AuthPageContent } from '@/components/auth-page-content';
import { BootstrapErrorScreen } from '@/components/bootstrap-error-screen';
import { CloudLinkConsent } from '@/components/cloud-link-consent';
import { CloudLinkRequired } from '@/components/cloud-link-required';
import { DesktopDesignSystemPreview } from '@/components/desktop-design-system-preview';
import { DesktopShell } from '@/components/desktop-shell';
import { LibraryPageContent } from '@/components/library-page/library-page-layer';
import { PagesPreview } from '@/components/library-page/pages-preview';
import { McpElicitationHost } from '@/components/mcp-elicitation-host';
import { SettingsPage } from '@/components/settings-page';
import {
  CrowlingoAccountProvider,
  crowlingoAccountOf,
} from '@/components/sidebar/crowlingo-account';
import { AppDialogHost } from '@/features/apps/app-dialog';

import { PictureInPictureStack } from '@/features/picture-in-picture/picture-in-picture-stack';
import { appHomeDeepLink, appHomeOf, LIBRARY_APP_PARAM } from '@/lib/app-home';
import { AppearanceProvider } from '@/lib/appearance/appearance-provider';
import { installPreviewWorkspaceDirectory } from '@/lib/assistants/directory';
import type { LocalWorkspace } from '@/lib/local-conversations';
import { isLibraryPagePath } from '@/lib/navigation';
import { parsePluginSection } from '@/lib/plugins/plugin-sections';

import { runtimeServices } from '@/lib/runtime-services';
import { parseSettingsSection } from '@/lib/settings-page/sections';
import { SIGNIN_PAGE } from '@/lib/sign-out';
import { navigateApp, useAppPath } from '@/vite/navigation-store';
import { useViewingSessionKey } from '@/vite/use-viewing-session-key';

/** Settings' page for an address, with what `?section=` and its sub-pages name. */
function SettingsRoute({ url, email, name }: { url: URL; email: string; name: string }) {
  const parameters = url.searchParams;
  return (
    <SettingsPage
      connection={parameters.get('connection')}
      email={email}
      initialProfile={parameters.get('profile')}
      initialSection={parseSettingsSection(parameters.get('section'))}
      name={name}
      plugin={parameters.get('plugin')}
      pluginView={parsePluginSection(parameters.get('view'))}
    />
  );
}

/** A conversation opened on an artifact of its side panel (`?panel=artifact:<id>`). */
function parseInitialPanel(parameters: URLSearchParams): PanelView | undefined {
  const requestedPanel = z.string().min(1).max(500).safeParse(parameters.get('panel'));
  return requestedPanel.success &&
    isPanelView(requestedPanel.data) &&
    requestedPanel.data.startsWith('artifact:')
    ? requestedPanel.data
    : undefined;
}

function AuthScreen() {
  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <AuthPageContent
          description="Pick up your agent threads from any device."
          title="Sign in to Crowdex"
        >
          <SignInButton />
        </AuthPageContent>
      </div>
    </main>
  );
}

/** `/desktop-preview…` renders the shell with preview data, signed in or not. */
function isPreviewPath(pathname: string) {
  return pathname === '/desktop-preview' || pathname.startsWith('/desktop-preview/');
}

/**
 * A preview started at `/desktop-preview` keeps previewing the library pages it opens
 * (`/desktop-preview/settings?section=…` opens its real route).
 */
let previewSession = false;

/** The real route a preview's library page address stands for, if it is one. */
function previewLibraryRoute(pathname: string, search: string): string | null {
  if (pathname === '/desktop-preview/settings') return `/settings${search}`;
  return null;
}

/** Settings (Plugins and Connections included) over the preview shell, with the boards' data. */
function PreviewLibraryPage({ url }: { url: URL }) {
  return url.pathname === '/settings' ? (
    <SettingsRoute email="jonas@crowlingo.com" name="Jonas B." url={url} />
  ) : null;
}

/** Preview routes opening a given conversation (lot 6: a project's, a project task's). */
const PREVIEW_CHATS: Record<string, string> = {
  '/desktop-preview/project': '0b7e3c52-6a8d-4c6f-9a51-2f6d1e0a7b11',
  '/desktop-preview/project-task': 'preview-landing',
};

function PreviewScreen({ pathname, search }: { pathname: string; search: string }) {
  // The boards' workspaces and projects, before anything reads the directory.
  installPreviewWorkspaceDirectory();
  const libraryRoute = previewLibraryRoute(pathname, search);
  // Settings and Plugins answer with the boards' data, from before the shell asks for anything
  // until the preview leaves them; the data is loaded only here.
  const settingsOrPlugins = isLibraryPagePath(pathname);
  const libraryPreview = libraryRoute !== null || settingsOrPlugins;
  const [previewData, setPreviewData] = useState(false);
  useEffect(() => {
    if (!libraryPreview) return;
    let uninstall: (() => void) | null = null;
    let live = true;
    void import('@/lib/desktop-preview-settings').then(({ installSettingsPreview }) => {
      if (!live) return;
      uninstall = installSettingsPreview();
      setPreviewData(true);
    });
    return () => {
      live = false;
      uninstall?.();
      setPreviewData(false);
    };
  }, [libraryPreview]);
  useEffect(() => {
    if (libraryRoute !== null && previewData) navigateApp(libraryRoute, true);
  }, [libraryRoute, previewData]);
  if (libraryPreview && !previewData) return null;
  if (settingsOrPlugins) {
    return (
      <>
        <DesktopShell email="jonas@crowlingo.com" preview />
        <LibraryPageContent>
          <PreviewLibraryPage url={new URL(`${pathname}${search}`, 'https://crowdex.invalid')} />
        </LibraryPageContent>
        <AppToaster />
      </>
    );
  }
  if (pathname.startsWith('/desktop-preview/pages/'))
    return <PagesPreview screen={pathname.slice('/desktop-preview/pages/'.length)} />;
  if (pathname === '/desktop-preview/design-system') {
    return (
      <>
        <DesktopDesignSystemPreview />
        <AppToaster />
      </>
    );
  }
  return (
    <>
      <DesktopShell
        email="jonas@crowlingo.com"
        preview
        // Lot 6: the boards' project conversation (D65) and a task of a project (D68).
        initialChatId={PREVIEW_CHATS[pathname]}
        previewConversation={
          pathname === '/desktop-preview/chat' || PREVIEW_CHATS[pathname] !== undefined
        }
        previewSetup={pathname === '/desktop-preview/setup' ? 'workspace' : null}
      />
      {/* What an action says back (Undo of a removal, an archive), as the app does. */}
      <AppToaster />
    </>
  );
}

/** What the signed-in pages need of the session: the bootstrap and the workspaces it lists. */
type SessionContext = { bootstrap: AppBootstrap; workspaces: LocalWorkspace[] };

function isAuthPath(pathname: string): boolean {
  return pathname === SIGNIN_PAGE || pathname === '/sign-up';
}

/** Pages shown without a session: the previews, and the library pages a preview opens. */
function isOpenPath(pathname: string): boolean {
  return isPreviewPath(pathname) || (previewSession && isLibraryPagePath(pathname));
}

/**
 * Every page but the picture-in-picture: loads the session (the bootstrap names the server
 * profile, which decides where workspaces come from), sends a visitor to sign in, and shows the
 * matched page once there is a session.
 */
function SessionLayout() {
  const { pathname } = useLocation();
  const services = runtimeServices();
  const isAuthRoute = isAuthPath(pathname);
  const [bootstrap, setBootstrap] = useState<AppBootstrap | null | undefined>();
  const [workspaces, setWorkspaces] = useState<LocalWorkspace[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Retry after a failed bootstrap: the attempt reloads it.
  const [attempt, setAttempt] = useState(0);
  const [retrying, setRetrying] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies(attempt): the retry button bumps the attempt to load the bootstrap again.
  useEffect(() => {
    let active = true;
    void services
      .loadBootstrap()
      .then(
        async (nextBootstrap) =>
          [nextBootstrap, nextBootstrap === null ? [] : await services.listWorkspaces()] as const,
      )
      .then(([nextBootstrap, nextWorkspaces]) => {
        if (!active) return;
        setBootstrap(nextBootstrap);
        setWorkspaces(nextWorkspaces);
        setError(null);
        setRetrying(false);
        // The cloud-link consent comes back to itself once signed in. Previews need no session.
        if (
          nextBootstrap === null &&
          !isAuthRoute &&
          !previewSession &&
          !isPreviewPath(window.location.pathname)
        )
          navigateApp(
            window.location.pathname === '/cloud-link'
              ? signinPath('/cloud-link', window.location.search, SIGNIN_PAGE)
              : SIGNIN_PAGE,
            true,
          );
        if (nextBootstrap !== null && isAuthRoute)
          navigateApp(afterSignIn(window.location.search), true);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(String(cause));
        setRetrying(false);
      });
    return () => {
      active = false;
    };
  }, [attempt, isAuthRoute, services]);

  if (isPreviewPath(pathname)) previewSession = true;
  // Accounts are created in Crowlingo's accounts (Zitadel), not here: its route sends to sign-in.
  if (isOpenPath(pathname) || pathname === '/sign-up') return <Outlet />;
  if (isAuthRoute && bootstrap === null) return <AuthScreen />;
  if (error)
    return (
      <BootstrapErrorScreen
        details={error}
        onRetry={() => {
          setRetrying(true);
          setAttempt((current) => current + 1);
        }}
        retrying={retrying}
      />
    );
  if (bootstrap === undefined)
    return (
      <main className="grid min-h-dvh place-items-center text-sm text-muted-foreground">
        Loading Crowdex…
      </main>
    );
  if (bootstrap === null) return <AuthScreen />;
  return <Outlet context={{ bootstrap, workspaces } satisfies SessionContext} />;
}

/** `/sign-up`: accounts are created in Crowlingo's accounts, so it is the sign-in. */
function SignUpRoute() {
  const { search } = useLocation();
  return <Navigate replace to={`/sign-in${search}`} />;
}

/** `/cloud-link`: a machine's server asks to be linked to this Crowdex Cloud account. */
function CloudLinkRoute() {
  const { bootstrap } = useOutletContext<SessionContext>();
  const [parameters] = useSearchParams();
  return <CloudLinkConsent email={bootstrap.user.email} parameters={parameters} />;
}

/**
 * The workspace (`/desktop`, and any other address): the shell, with the library page the
 * address names (Settings, artifacts, an app's home) over it, the shell staying mounted. The
 * preview (`/desktop-preview…`, and the library pages it opens) is the shell with preview data,
 * signed in or not: the same element answers both routes.
 */
function WorkspaceRoute() {
  const path = useAppPath();
  const viewingSession = useViewingSessionKey(path);
  const url = new URL(path, 'https://crowdex.invalid');
  // A preview keeps previewing the library pages it opens.
  if (isOpenPath(url.pathname))
    return <PreviewScreen pathname={url.pathname} search={url.search} />;
  return <SignedInWorkspace url={url} viewingSession={viewingSession} />;
}

function SignedInWorkspace({
  url,
  viewingSession,
}: {
  url: URL;
  viewingSession: ReturnType<typeof useViewingSessionKey>;
}) {
  const { bootstrap, workspaces } = useOutletContext<SessionContext>();
  // Signed in on the sign-in page: the session's effect leaves it.
  if (isAuthPath(url.pathname)) return null;
  const requestedThreadId = z.uuid().safeParse(url.searchParams.get('threadId'));
  const requestedWorkspaceId = z.uuid().safeParse(url.searchParams.get('workspaceId'));
  const requestedWorkspace = requestedWorkspaceId.success
    ? workspaces.find((workspace) => workspace.id === requestedWorkspaceId.data)
    : undefined;
  const initialPanelView = parseInitialPanel(url.searchParams);
  const newSession = url.searchParams.get('newSession') === 'true';
  // No Home page and no last conversation brought back: launch opens a new conversation;
  // only a conversation the address names (a link, a reload) is restored.
  const restoredThreadId = !newSession && requestedThreadId.success ? requestedThreadId.data : null;
  const permissions = new Set(bootstrap.permissions);
  const shell = (
    <DesktopShell
      key={viewingSession.key}
      email={bootstrap.user.email}
      initialChatId={restoredThreadId ?? viewingSession.chatId}
      initialPanelView={initialPanelView}
      initialWorkspace={requestedWorkspace?.rootPath}
      pluginPermissions={permissions}
      restoreInitialThread={restoredThreadId !== null}
      usePersonalWorkspace={
        url.searchParams.get('personalWorkspace') === 'true' && requestedWorkspace === undefined
      }
    />
  );
  let libraryPage = null;
  const appHome = appHomeOf(`${url.pathname}${url.search}`);
  if (appHome !== null) {
    libraryPage = (
      <AppHomePage
        deepLink={appHomeDeepLink(`${url.pathname}${url.search}${url.hash}`)}
        item={appHome.item}
        plugin={appHome.plugin}
        route={appHome.route}
      />
    );
  } else if (url.pathname === '/settings') {
    libraryPage = (
      <SettingsRoute email={bootstrap.user.email} name={bootstrap.user.name} url={url} />
    );
  } else if (url.pathname === '/artifacts') {
    libraryPage = (
      <ArtifactsPage
        app={url.searchParams.get(LIBRARY_APP_PARAM)}
        initialArtifactId={url.searchParams.get('artifactId')}
      />
    );
  }

  const app = (
    <CrowlingoAccountProvider account={crowlingoAccountOf(bootstrap)}>
      {shell}
      {libraryPage && <LibraryPageContent>{libraryPage}</LibraryPageContent>}
      <McpElicitationHost />
      <AppDialogHost />
      <AppToaster />
    </CrowlingoAccountProvider>
  );
  // A machine's server works with its Crowdex Cloud account; the cloud deployment is that account.
  return bootstrap.profile === 'local' ? <CloudLinkRequired>{app}</CloudLinkRequired> : app;
}

/**
 * The renderer's routes, the same in every host (a browser, the Desktop's window on the local
 * server, the preview). The workspace route takes every other address: the shell decides what
 * an unknown one shows, as before.
 */
export const appRoutes: RouteObject[] = [
  // The Desktop's picture-in-picture window loads its own page of the renderer.
  { path: '/picture-in-picture', element: <PictureInPictureStack /> },
  {
    element: (
      <AppearanceProvider>
        <SessionLayout />
      </AppearanceProvider>
    ),
    children: [
      { path: '/desktop-preview/*', element: <WorkspaceRoute /> },
      { path: '/sign-up', element: <SignUpRoute /> },
      { path: '/cloud-link', element: <CloudLinkRoute /> },
      // Workspaces have no page any more: an old link opens the app.
      { path: '/workspaces/*', element: <Navigate replace to="/" /> },
      { path: '*', element: <WorkspaceRoute /> },
    ],
  },
];
